import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// --- NEW helper: case-insensitive / space-insensitive key lookup + logical field mapper ---
const getCaseInsensitive = (obj, ...candidates) => {
  if (!obj) return undefined;
  const norm = s => String(s).replace(/\s+/g, '').toLowerCase();
  const map = {};
  for (const k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) map[norm(k)] = obj[k];
  }
  for (const c of candidates) {
    const hit = map[norm(c)];
    if (hit !== undefined) return hit;
  }
  return undefined;
};
const getIngramField = (row, logical) => {
  switch (logical) {
    case 'item':         return getCaseInsensitive(row, 'ITEM', '#', 'Item');
    case 'product':      return getCaseInsensitive(row, 'PRODUCT', 'Product');
    case 'qtyAvailable': return getCaseInsensitive(row, 'QTY AVAILABLE', 'Qty Available', 'Qty');
    case 'qtyNeeded':    return getCaseInsensitive(row, 'QTY NEEDED', 'Order Qty');
    case 'yourOffer':    return getCaseInsensitive(row, 'YOUR OFFER', 'Offer');
    case 'newOffer':     return getCaseInsensitive(row, 'New offer', 'New Offer');
    case 'bestOffer':    return getCaseInsensitive(row, 'Best Offer', 'BEST OFFER');
    case 'priceTotal':   return getCaseInsensitive(row, 'PRICE TOTAL', 'Total');
    default:             return undefined;
  }
};
// Helper functions - copied from Logic.js to maintain the same matching functionality
const extractGrade = (name) => {
  if (!name) return null;
  const gradeMatch = name.match(/\[(.*?)\]/);
  if (!gradeMatch) return null;

  const grade = gradeMatch[1];
  if (grade === 'Brand New') return 'BN';
  if (grade === 'Grade A') return 'GA';
  if (grade === 'Grade B') return 'GB';
  if (grade === 'Open Box') return 'OB';
  if (grade === 'Like New') return 'LN';
  return null;
};

const extractStorage = (name) => {
  if (!name || typeof name !== 'string') return null;

  // Check for storage in parentheses with space (multiple storage)
  const multiStorageMatch = name.match(/\((\d+(?:GB|TB)?\s+\d+(?:GB|TB)?)\)/i);
  if (multiStorageMatch) {
    const storageStr = multiStorageMatch[1];
    const sizes = storageStr.split(/\s+/)
      .map(s => {
        const match = s.match(/(\d+)(?:GB|TB)?/i);
        return match ? parseInt(match[1]) : null;
      })
      .filter(s => s !== null);
    
    return {
      sizes,
      isMultiple: true,
      originalMultiStorage: multiStorageMatch[1]
    };
  }

  // Single storage in parentheses or without
  const storageMatch = name.match(/(?:\()?(\d+)\s*(GB|TB)(?:\))?/i);
  if (storageMatch) {
    return {
      size: parseInt(storageMatch[1]),
      unit: storageMatch[2].toUpperCase(),
      isMultiple: false
    };
  }
  // Check for common storage values (128, 256, 512) without GB/TB unit
  const commonStorageMatch = name.match(/\s(64|128|256|512)\s/);
  if (commonStorageMatch) {
    return {
      size: parseInt(commonStorageMatch[1]),
      unit: "GB", // Assume GB for these common values
      isMultiple: false
    };
  }
  return null;
};

const normalizeProductName = (name) => {
  if (!name || typeof name !== 'string') return { normalized: '', storage: null };
  
  // Extract storage before normalization
  const storage = extractStorage(name);
  
  let cleaned = name;
  
  // Only remove text between " -" and parenthesis if both patterns exist
  if (cleaned.includes(' -') && cleaned.includes('(')) {
    const dashIndex = cleaned.indexOf(' -');
    const parenIndex = cleaned.indexOf('(', dashIndex);
    if (parenIndex > dashIndex) {
      cleaned = cleaned.substring(0, dashIndex) + ' ' + cleaned.substring(parenIndex);
    }
  }
  
  cleaned = cleaned
    .replace(/5G\s+/g, '') // Remove 5G with trailing space
    .replace(/\[[^\]]*\]/g, '') // Remove grade in square brackets
    .replace(/\((\d+)\s*GB\)/gi, '$1') // Keep numbers, remove (GB) from parentheses
    .replace(/(\d+)\s*GB/gi, '$1') // Keep numbers, remove GB without parentheses
    .replace(/\((\d+)\s*TB\)/gi, '$1TB') // Keep numbers with TB unit
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim(); // Remove leading/trailing spaces
  return {
    normalized: cleaned,
    storage
  };
};

const getBaseProductName = (name) => {
  if (!name) return '';
  return name
    .replace(/\s*\[[^\]]*\]\s*/, '') // Remove grade in brackets
    .replace(/\s*\(\d+(?:GB)?\)\s*/i, '') // Remove storage in parentheses
    .replace(/\s*\([A-Z0-9,]+\)\s*/i, '') // Remove model numbers in parentheses
    .trim();
};

const cleanIngramProduct = (product) => {
  if (!product) return '';
  const parts = product.split(' -');
  return parts[0].trim();
};

const getNameVariations = (normalizedName) => {
  const variations = [normalizedName];
  
  // If name contains "Plus", add version with "+"
  if (normalizedName.includes(' Plus')) {
    variations.push(normalizedName.replace(' Plus', '+'));
  }
  // If name contains "+", add version with "Plus"
  else if (normalizedName.includes('+')) {
    variations.push(normalizedName.replace('+', ' Plus'));
  }

  // Handle SE 3rd Gen variation
  if (normalizedName.includes(' SE ')) {
    const seMatch = normalizedName.match(/SE (\d)(?:rd|th|st|nd) Gen/i);
    if (seMatch) {
      variations.push(normalizedName.replace(seMatch[0], `SE${seMatch[1]}`));
    }
  }

  return variations;
};

// Only modify the "name match" part of the function
const processModelMatches = (ocProducts, ingramData, enableChatGPTVerification = false) => {
  const results = [];
  const modelMatches = new Map(); // model -> { storage -> products }
  const nameMatches = new Map();
  const processedIngramIndices = new Set();
  let nameMatchesToVerify = []; // Store potential name matches for verification

  // First organize OC products by normalized name
  const normalizedOcProducts = new Map(); // normalized name -> product info

  ocProducts.forEach(ocProduct => {
    if (!ocProduct.Name) return;
    
    const { normalized: normalizedName, storage } = normalizeProductName(ocProduct.Name);
    const grade = extractGrade(ocProduct.Name);
    const quantity = parseInt(ocProduct.Qty) || 0;

    if (!normalizedOcProducts.has(normalizedName)) {
      normalizedOcProducts.set(normalizedName, {
        name: normalizedName,
        storage: storage,
        grades: [],
        quantities: { BN: 0, GB: 0, GA: 0, OB: 0, LN: 0 },
        originalProducts: []
      });
    }

    const productInfo = normalizedOcProducts.get(normalizedName);
    if (grade) {
      productInfo.grades.push(grade);
      productInfo.quantities[grade] += quantity;
    }
    productInfo.originalProducts.push(ocProduct);
  });

  // First collect all valid models from OC products
  ocProducts.forEach(ocProduct => {
    if (!ocProduct.Model || 
        ocProduct.Model.length <= 3 || 
        String(ocProduct.Model).includes(' ')) {
      return;
    }

    if (!modelMatches.has(ocProduct.Model)) {
      modelMatches.set(ocProduct.Model, new Map()); // storage -> products map
    }
    
    // Get storage info
    const storage = extractStorage(ocProduct.Name);
    const storageKey = storage 
      ? (storage.isMultiple 
          ? storage.sizes.join(',')
          : (storage.unit === 'TB' ? `${storage.size}${storage.unit}` : `${storage.size}`))
      : 'NO_STORAGE';
    
    if (!modelMatches.get(ocProduct.Model).has(storageKey)) {
      modelMatches.get(ocProduct.Model).set(storageKey, {
        products: [],
        ingramIndices: new Set()
      });
    }
    
    modelMatches.get(ocProduct.Model).get(storageKey).products.push(ocProduct);
  });

  // Process each Ingram product
  ingramData.forEach((ingramProduct, index) => {
    // Handle both uppercase and lowercase field names for flexibility
    const productField = getIngramField(ingramProduct, 'product');
    const cleanedProduct = cleanIngramProduct(productField);
    const ingramStorage = extractStorage(productField);
    const firstWord = cleanedProduct.split(' ')[0];

    
    modelMatches.forEach((storageMap, model) => {
      if (model === firstWord) return;
      
      const modelStr = String(model);
      const withoutDsORDsn = modelStr.replace(/\/DSN?$/, '');
      const modelExists = cleanedProduct.includes(modelStr) || 
        (modelStr.startsWith('SM-') && cleanedProduct.includes(modelStr + '/DS')) || 
        (modelStr.startsWith('SM-') && cleanedProduct.includes(modelStr + '/DSN')) ||
        (modelStr.startsWith('SM-') && modelStr !== withoutDsORDsn && cleanedProduct.includes(withoutDsORDsn));
    
      if (modelExists) {
        storageMap.forEach((data, storageKey) => {
          let storageMatches = false;

          if (ingramStorage && ingramStorage.originalMultiStorage && ingramStorage.originalMultiStorage.includes(' ')) {
            storageMatches = ingramStorage.sizes.every(size => {
              return cleanedProduct.includes(size.toString());
            });
          }
          else if (storageKey === 'NO_STORAGE') {
            storageMatches = true;
          } else if (ingramStorage || storageKey) {
            if (storageKey.includes(',')) {
              const requiredSizes = storageKey.split(',').map(size => parseInt(size.trim()));
              storageMatches = requiredSizes.every(size => {
                const sizeInName = cleanedProduct.includes(size.toString());
                return sizeInName;
              });
            }
            else if (ingramStorage && Array.isArray(ingramStorage.sizes)) {
              storageMatches = ingramStorage.sizes.includes(parseInt(storageKey));
            }
            else if (ingramStorage && ingramStorage.size !== undefined && ingramStorage.unit) {
              storageMatches = 
                (ingramStorage.unit === 'TB' && storageKey === `${ingramStorage.size}${ingramStorage.unit}`) ||
                (ingramStorage.unit === 'GB' && ingramStorage.size !== undefined && storageKey === ingramStorage.size.toString());
            } else {
              storageMatches = false;
            }
          } else {
            storageMatches = false;
          }

          if (storageMatches) {
            data.ingramIndices.add(index);
          }
        });
      }
    });
  });

  // Process model matches first
  modelMatches.forEach((storageMap, model) => {
    storageMap.forEach((data, storageKey) => {
      if (data.ingramIndices.size === 0) return;

      const gradeCounts = { BN: 0, GB: 0, GA: 0, OB: 0, LN: 0 };
      let totalQuantity = 0;

      data.products.forEach(product => {
        const grade = extractGrade(product.Name);
        if (grade) {
          const quantity = parseInt(product.Qty) || 0;
          gradeCounts[grade] += quantity;
          totalQuantity += quantity;
        }
      });

      const baseName = getBaseProductName(data.products[0].Name);
      const storage = extractStorage(data.products[0].Name);
      const storageStr = storage ? ` (${storage.size}${storage.unit})` : '';

      const matchingIngramProducts = Array.from(data.ingramIndices).map(index => {
            const row = ingramData[index];
            const prod = getIngramField(row, 'product');
            return {
                original: prod,
                cleaned: cleanIngramProduct(prod),
                index,
                ingramItem: row
            };
        });

      results.push({
        model,
        productName: `${baseName}${storageStr} (${model})`,
        totalQuantity,
        matchType: 'model',
        storage: storage ? `${storage.size}${storage.unit}` : 'N/A',
        gradeCounts: `BN:${gradeCounts.BN},GA:${gradeCounts.GA},GB:${gradeCounts.GB},OB:${gradeCounts.OB},LN:${gradeCounts.LN}`,
        matchingProducts: [
          'OC Products:',
          ...data.products.map(p => p.Name),
          '',
          'Matching Ingram Products:',
          ...matchingIngramProducts.map(p => p.original)
        ].join('\n'),
        ingramIndices: Array.from(data.ingramIndices),
        storeProductIds: data.products.map(p => p['Product Id']),
        ingramItems: matchingIngramProducts.map(p => p.ingramItem)
      });

      // Mark these Ingram products as processed
      data.ingramIndices.forEach(idx => processedIngramIndices.add(idx));
    });
  });

  // Now do name matching for unmatched products
  normalizedOcProducts.forEach((productInfo, normalizedName) => {
    ingramData.forEach((ingramProduct, ingramIndex) => {
      if (processedIngramIndices.has(ingramIndex)) return;

      // Handle both uppercase and lowercase field names for flexibility
      const productField = getIngramField(ingramProduct, 'product');
      const { normalized: normalizedIngramName, storage: ingramStorage } = normalizeProductName(productField);
      
      // Get all possible variations of the name and make comparison case-insensitive
      const variations = getNameVariations(normalizedName);
      const normalizedIngramLower = normalizedIngramName.toLowerCase();
      const nameMatch = variations.some(variant =>{
        return normalizedIngramLower.startsWith(variant.toLowerCase())
      });

      if (nameMatch) {
        // Use the storage that was extracted during normalization
        const storageMatches = !productInfo.storage || !ingramStorage || 
          (productInfo.storage.isMultiple && productInfo.storage.sizes.every(size => 
            ingramStorage.isMultiple ? ingramStorage.sizes.includes(size) : ingramStorage.size === size
          )) ||
          (!productInfo.storage.isMultiple && !ingramStorage.isMultiple && productInfo.storage.size === ingramStorage.size);

          if (nameMatch && storageMatches) {
            // Store for verification later instead of immediately creating a match
            nameMatchesToVerify.push({
              normalizedName,
              productInfo,
              ingramIndex,
              ingramProduct,
              storeProductNames: productInfo.originalProducts.map(p => p.Name),
              ingramProductName: ingramProduct.Product || ingramProduct.PRODUCT
            });
          }
      }
    });
  });

  // Update the verification function to use batch verification
  const verifyMatchesWithChatGPT = async (matchesToVerify) => {
    if (!matchesToVerify || matchesToVerify.length === 0) return [];
    
    try {
      // Use a smaller batch size to avoid overwhelming the API
      const BATCH_SIZE = 5;
      const results = [];
      
      // Split into batches to process
      for (let i = 0; i < matchesToVerify.length; i += BATCH_SIZE) {
        const batch = matchesToVerify.slice(i, i + BATCH_SIZE);
        console.log(`Processing batch ${i/BATCH_SIZE + 1} of ${Math.ceil(matchesToVerify.length/BATCH_SIZE)}`);
        
        const apiUrl = process.env.NODE_ENV === 'production' 
          ? '/api/batch-verify-match'
          : 'http://localhost:8000/api/batch-verify-match';
          
        // Format the matches for the API
        const batchRequest = {
          matches: batch.map(match => ({
            store_product_names: match.storeProductNames,
            ingram_product_name: match.ingramProductName
          }))
        };
        
        try {
          const response = await axios.post(apiUrl, batchRequest);
          
          if (response.data && response.data.results) {
            // Map results back to our format
            const batchResults = response.data.results.map((result, idx) => ({
              ...batch[idx],
              isMatched: result.matched
            }));
            
            results.push(...batchResults);
          } else {
            // Handle empty or invalid response
            console.error('Invalid response from verification API:', response.data);
            // Just add the batch with isMatched = false
            results.push(...batch.map(match => ({ ...match, isMatched: false })));
          }
        } catch (error) {
          console.error(`Error verifying batch ${i/BATCH_SIZE + 1}:`, error.message);
          // For this batch, just set isMatched to false
          results.push(...batch.map(match => ({ ...match, isMatched: false })));
        }
        
        // Add a small delay between batches to avoid overwhelming the server
        if (i + BATCH_SIZE < matchesToVerify.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error in batch verification process:', error);
      // Default to all false if there's an error
      return matchesToVerify.map(match => ({ ...match, isMatched: false }));
    }
  };

  // After processing all potential matches (both model and name-based),
  // if we have name matches to verify, return a Promise to handle the async verification
  // Change 2: Update the conditional to use the parameter instead of state variable
  if (nameMatchesToVerify.length > 0) {
    return new Promise(async (resolve) => {
      console.log(`Found ${nameMatchesToVerify.length} potential name matches`);
      
      // If ChatGPT verification is disabled, directly add all potential matches
      if (!enableChatGPTVerification) {
        console.log('ChatGPT verification disabled - using all potential matches');
        
        // Directly add all potential matches without verification
        for (const match of nameMatchesToVerify) {
          if (!nameMatches.has(match.normalizedName)) {
            nameMatches.set(match.normalizedName, {
              productInfo: match.productInfo,
              ingramIndices: new Set()
            });
          }
          
          nameMatches.get(match.normalizedName).ingramIndices.add(match.ingramIndex);
          processedIngramIndices.add(match.ingramIndex);
        }
      } else {
        // Use ChatGPT verification as before
        console.log(`Verifying ${nameMatchesToVerify.length} potential name matches with ChatGPT`);
        
        // Batch verify matches instead of one by one
        const verifiedMatches = await verifyMatchesWithChatGPT(nameMatchesToVerify);
        
        // Process verified matches
        for (const match of verifiedMatches) {
          if (match.isMatched) {
            console.log(`ChatGPT verified match for: ${match.ingramProductName}`);
            
            // Add to name matches if verified
            if (!nameMatches.has(match.normalizedName)) {
              nameMatches.set(match.normalizedName, {
                productInfo: match.productInfo,
                ingramIndices: new Set()
              });
            }
            
            nameMatches.get(match.normalizedName).ingramIndices.add(match.ingramIndex);
            processedIngramIndices.add(match.ingramIndex);
          } else {
            console.log(`ChatGPT verification failed for: ${match.ingramProductName}`);
          }
        }
      }
      
      // Now process name matches as before after verification
      nameMatches.forEach((match, normalizedName) => {
        const productInfo = match.productInfo;
        
        Array.from(match.ingramIndices).forEach(ingramIndex => {
          const ingramProduct = ingramData[ingramIndex];
          results.push({
            model: normalizedName,
            productName: getIngramField(ingramProduct, 'product'),
            totalQuantity: Object.values(productInfo.quantities).reduce((sum, qty) => sum + qty, 0),
            matchType: 'name',
            storage: extractStorage(getIngramField(ingramProduct, 'product'))?.size || 'N/A',
            gradeCounts: `BN:${productInfo.quantities.BN},GA:${productInfo.quantities.GA},GB:${productInfo.quantities.GB},OB:${productInfo.quantities.OB},LN:${productInfo.quantities.LN}`,
            matchingProducts: productInfo.originalProducts.map(p => p.Name).join('\n'),
            ingramIndices: [ingramIndex],
            storeProductIds: productInfo.originalProducts.map(p => p['Product Id']),
            ingramItems: [ingramProduct]
          });
        });
      });
      
      console.log(results);
      resolve(results);
    });
  }
  
  // Process name matches normally if we didn't need verification
  nameMatches.forEach((match, normalizedName) => {
    const productInfo = match.productInfo;
    
    Array.from(match.ingramIndices).forEach(ingramIndex => {
      const ingramItem = ingramData[ingramIndex];
      results.push({
        model: normalizedName,
        productName: getIngramField(ingramItem, 'product'),
        totalQuantity: Object.values(productInfo.quantities).reduce((sum, qty) => sum + qty, 0),
        matchType: 'name',
        storage: extractStorage(getIngramField(ingramItem, 'product'))?.size || 'N/A',
        gradeCounts: `BN:${productInfo.quantities.BN},GA:${productInfo.quantities.GA},GB:${productInfo.quantities.GB},OB:${productInfo.quantities.OB},LN:${productInfo.quantities.LN}`,
        matchingProducts: productInfo.originalProducts.map(p => p.Name).join('\n'),
        ingramIndices: [ingramIndex],
        storeProductIds: productInfo.originalProducts.map(p => p['Product Id']),
        ingramItems: [ingramItem]
      });
    });
  });

  console.log(results);
  return results;
};

// Extract auction date from filename (format: VFD24012025 -> 24-01-2025)
const extractAuctionDate = (filename) => {
  const dateMatch = filename.match(/(?:VFD|ABC)(\d{2})(\d{2})(\d{4})/);
  if (dateMatch) {
    const [_, day, month, year] = dateMatch;
    return `${year}-${month}-${day}`; // Format for MySQL date: YYYY-MM-DD
  }
  return null;
};

// Extract SKU if possible
const extractSKU = (productDescription) => {
  // Look for common SKU patterns (alphanumeric with dashes/dots)
  const skuMatch = productDescription.match(/[A-Z0-9]+-[A-Z0-9]+(-[A-Z0-9]+)?/i);
  if (skuMatch) return skuMatch[0];
  
  // Fallback: try to find model number for Samsung products
  const samsungMatch = productDescription.match(/SM-[A-Z0-9]+(?:\/(?:DS|DSN))?/i);
  if (samsungMatch) return samsungMatch[0];
  
  return null;
};

// New BestOffer Component
const BestOfferComponent = () => {
  const [storeProductData, setStoreProductData] = useState([]);
  const [ingramData, setIngramData] = useState([]);
  const [matchResults, setMatchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sourceFilename, setSourceFilename] = useState('');
  const [isDatabaseConnected, setIsDatabaseConnected] = useState(false);
  const [enableChatGPT, setEnableChatGPT] = useState(false); // Default to OFF to save costs

  // Check database connection on component mount
  useEffect(() => {
    const checkDbConnection = async () => {
      try {
        const apiUrlDb = process.env.NODE_ENV === 'production' 
          ? '/api/check-db-connection'
          : 'http://localhost:8000/api/check-db-connection';
        const response = await axios.get(apiUrlDb);
        setIsDatabaseConnected(response.data.connected);
        if (!response.data.connected) {
          setError('Database connection failed. Please check server configuration.');
        }
      } catch (err) {
        console.error('Error checking database connection:', err);
        setError('Could not verify database connection. Server may be down.');
        setIsDatabaseConnected(false);
      }
    };

    checkDbConnection();
  }, []);

  // Handle file uploads
  const handleFileUpload = (file, type) => {
    if (!file) return;

    if (type === 'store') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          setStoreProductData(jsonData);
          setError('');
          toast.success('Store products loaded successfully');
        } catch (error) {
          console.error('Error processing store XLSX:', error);
          setError('Error processing store products file');
          toast.error('Failed to load store products file');
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (type === 'ingram') {
      const reader = new FileReader();
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      // Store the filename for later use in database
      setSourceFilename(file.name);
      
      reader.onload = (e) => {
        try {
          let jsonData;
          
          if (fileExtension === 'xlsx') {
            // Handle XLSX file
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            jsonData = XLSX.utils.sheet_to_json(worksheet);
          }
          
          setIngramData(jsonData);
          setError('');
          toast.success('Ingram data loaded successfully');
        } catch (error) {
          console.error('Error processing file:', error);
          setError('Error processing Ingram file');
          toast.error('Failed to load Ingram file');
        }
      };
      
      reader.readAsArrayBuffer(file);
    }
  };

  // Update processData to handle potential promise from processModelMatches
const processData = () => {
  if (!storeProductData.length || !ingramData.length) {
    setError('Please upload both files first');
    return;
  }

  try {
    // Get matching results - may be a promise if doing ChatGPT verification
    const matchingResultsOrPromise = processModelMatches(storeProductData, ingramData, enableChatGPT);
    
    // Handle both cases - direct results or Promise
    Promise.resolve(matchingResultsOrPromise).then(matchingResults => {
      // The rest of your code remains unchanged
      const ingramProductRows = [];
      
      // Process through all match results
      matchingResults.forEach(result => {
          // For each Ingram item that matched, create a separate entry
          result.ingramItems.forEach(ingramItem => {
            // Extract only the store product names
            let storeProductNames = [];
            
            if (result.matchingProducts) {
              const lines = result.matchingProducts.split('\n');
              
              // Different handling based on match type
              if (result.matchType === 'name') {
                // For name matches, the matchingProducts is directly a list of store products
                storeProductNames = lines.filter(line => line.trim());
              } else {
                // For model matches, need to extract from the "OC Products:" section
                let collectingStoreProducts = false;
                
                for (const line of lines) {
                  // Start collecting store products after this marker
                  if (line === 'OC Products:') {
                    collectingStoreProducts = true;
                    continue;
                  }
                  // Stop collecting when we hit an empty line or the next section
                  else if (line === '' || line === 'Matching Ingram Products:') {
                    collectingStoreProducts = false;
                    continue;
                  }
                  
                  // Only add lines when we're in the OC Products section
                  if (collectingStoreProducts && line.trim()) {
                    storeProductNames.push(line.trim());
                  }
                }
              }
            }
            
            ingramProductRows.push({
              ingramProduct: ingramItem,
              productName: getIngramField(ingramItem, 'product'),
              matchType: result.matchType,
              storage: result.storage,
              // The product IDs that matched with this Ingram product
              matchedProductIds: result.storeProductIds,
              // The product names that matched with this Ingram product
              matchedProductNames: storeProductNames
            });
          });
        });
        
        setMatchResults(ingramProductRows);
        setError('');
        toast.success(`Found ${ingramProductRows.length} Ingram products with matches`);
      });
    } catch (error) {
      console.error('Error processing data:', error);
      setError('Error processing data');
      toast.error('Error during product matching');
    }
  };

  // Save the matched data to the database
  const saveToDatabase = async () => {
    if (!matchResults.length) {
      setError('No matching results to save');
      return;
    }

    setLoading(true);
    try {
      // Extract auction date from filename
      const auctionDate = extractAuctionDate(sourceFilename);
      
      if (!auctionDate) {
        setError('Could not extract auction date from filename. Expected format: VFDddmmyyyy');
        setLoading(false);
        return;
      }

      // Prepare records for database - each matchResult is already one Ingram product
      const records = matchResults.map(result => {
          const row = result.ingramProduct;
          const desc = getIngramField(row, 'product') || '';
          return {
            auction_date: auctionDate,
            item_number: getIngramField(row, 'item') || 'N/A',
            store_product_ids: JSON.stringify(result.matchedProductIds),
            product_description: desc,
            sku: extractSKU(desc) || '',
            grade: extractGrade(desc) || '',
            qty_available: parseInt(getIngramField(row, 'qtyAvailable')) || 0,
            qty_needed: parseInt(getIngramField(row, 'qtyNeeded')) || 0,
            your_offer: parseFloat(getIngramField(row, 'yourOffer')) || 0,
            new_offer: parseFloat(getIngramField(row, 'newOffer')) || 0,
            best_offer: parseFloat(getIngramField(row, 'bestOffer')) || 0,
            source_file: sourceFilename
         };
        });

      // Send to API endpoint
      const apiUrlSaveAuctionData = process.env.NODE_ENV === 'production' 
          ? '/api/save-auction-data'
          : 'http://localhost:8000/api/save-auction-data';
      const response = await axios.post(apiUrlSaveAuctionData, { records });
      
      setSuccess(`Successfully saved ${records.length} records to the database`);
      toast.success(`Saved ${records.length} records to the database`);
    } catch (error) {
      console.error('Error saving to database:', error);
      setError('Error saving to database: ' + (error.response?.data?.message || error.message));
      toast.error('Failed to save to database');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <ToastContainer position="top-right" autoClose={5000} />
      <h1 className="text-2xl font-bold mb-6">Auction Best Offer Matcher</h1>
      
      {!isDatabaseConnected && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          Database connection error. Check server configuration.
        </div>
      )}
      
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Upload Files</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="border p-4 rounded">
            <label className="block mb-2 font-medium">Store Products (XLSX)</label>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => handleFileUpload(e.target.files[0], 'store')}
              className="block w-full text-sm border rounded p-2"
            />
            {storeProductData.length > 0 && (
              <div className="mt-2 text-sm text-green-600">
                Loaded {storeProductData.length} store products
              </div>
            )}
          </div>
          
          <div className="border p-4 rounded">
            <label className="block mb-2 font-medium">Ingram/Auction File (XLSX)</label>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => handleFileUpload(e.target.files[0], 'ingram')}
              className="block w-full text-sm border rounded p-2"
            />
            {ingramData.length > 0 && (
              <div className="mt-2 text-sm text-green-600">
                Loaded {ingramData.length} Ingram products
              </div>
            )}
          </div>
        </div>
        
        <div className="flex flex-wrap gap-4">
          <div className="mb-4 flex items-center">
            <label className="flex items-center cursor-pointer">
              <div className="relative">
                <input 
                  type="checkbox" 
                  className="sr-only" 
                  checked={enableChatGPT} 
                  onChange={() => setEnableChatGPT(!enableChatGPT)} 
                />
                <div className={`block w-14 h-8 rounded-full ${enableChatGPT ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition transform ${enableChatGPT ? 'translate-x-full' : ''}`}></div>
              </div>
              <div className="ml-3 text-gray-700 font-medium">
                {enableChatGPT ? 'ChatGPT Verification ON' : 'ChatGPT Verification OFF'}
              </div>
            </label>
            <div className="ml-2 text-sm text-gray-500">
              {enableChatGPT 
                ? 'More accurate matching but slower and uses API credits' 
                : 'Faster processing but may include false matches'}
            </div>
          </div>
          <button 
            onClick={processData}
            disabled={!storeProductData.length || !ingramData.length || loading}
            className={`px-4 py-2 rounded font-medium ${
              !storeProductData.length || !ingramData.length || loading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Match Products
          </button>
          
          <button 
            onClick={saveToDatabase}
            disabled={!matchResults.length || loading || !isDatabaseConnected}
            className={`px-4 py-2 rounded font-medium ${
              !matchResults.length || loading || !isDatabaseConnected
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {loading ? 'Saving...' : 'Save to Database'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {matchResults.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Match Results ({matchResults.length})</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border">
              <thead>
                <tr>
                  <th className="px-4 py-2 border">Ingram Product</th>
                  <th className="px-4 py-2 border">Match Type</th>
                  <th className="px-4 py-2 border">Storage</th>
                  <th className="px-4 py-2 border">Your Offer</th>
                  <th className="px-4 py-2 border">New Offer</th>
                  <th className="px-4 py-2 border">Best Offer</th>
                  <th className="px-4 py-2 border">Matched Store Products</th>
                  <th className="px-4 py-2 border">Store Product IDs</th>
                </tr>
              </thead>
              <tbody>
                {matchResults.map((result, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-4 py-2 border">{result.productName}</td>
                    <td className="px-4 py-2 border">{result.matchType}</td>
                    <td className="px-4 py-2 border">{result.storage}</td>
                    <td className="px-4 py-2 border">
                      {result.ingramProduct['YOUR OFFER']}
                    </td>
                    <td className="px-4 py-2 border">
                      {result.ingramProduct['New offer']}
                    </td>
                    <td className="px-4 py-2 border">
                      {result.ingramProduct['Best Offer']}
                    </td>
                    <td className="px-4 py-2 border max-w-xs truncate">
                      {result.matchedProductNames.join(', ')}
                    </td>
                    <td className="px-4 py-2 border">
                      {result.matchedProductIds && result.matchedProductIds.filter(id => id !== undefined).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BestOfferComponent;